import React, { FC, useMemo, useState, useContext } from 'react'

import { graphql, useFragment } from 'react-relay'
import { useMatch } from 'react-router'
import { differenceInCalendarDays } from 'date-fns'
import {
  format,
  utcToZonedTime,
  zonedTimeToUtc,
  getTimezoneOffset,
} from 'date-fns-tz'

import enUS from 'date-fns/locale/en-US'
import enGB from 'date-fns/locale/en-GB'

import { DEFAULT_TIMEZONE } from '@data/timezones'

import { ActorItem } from '../Actor/ActorItem'
import { EventCardPresentationsSection } from './EventCardPresentationsSection'
import { EventCardAttendeesSection } from './EventCardAttendeesSection'
import { AttendEventButton } from './AttendEventButton'
import { Button } from '../Button'
import { Card } from '../Card'
import { Element } from '../Element'
import { EmojiIcon } from '../EmojiIcon/EmojiIcon'
import { Icon } from '../Icon'
import { Link } from '../Link'
import { Stack } from '../Stack'
import { Text } from '../Text'
import { GraphQLImage, ImageKitImage } from '../GraphQLImage'
import { Image } from '../Image'

import { ServerContext } from '@data/ServerContext'
import { HOST_NAME } from '@data/config/url'

import {
  EventCard_event,
  EventCard_event$key,
} from './__generated__/EventCard_event.graphql'

const BACKGROUNDS = [
  'https://ik.imagekit.io/guild/social-card-backgrounds/purple-yellow_zbyzdUESq.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/pink-yellow_UvKUtm5f3.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/blue-purple_F4x1_B8WX.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/blue-teal_HwODg9qh1.png',
  'https://ik.imagekit.io/guild/social-card-backgrounds/purple-blue_t3kh8e78Bl.png',
]

export const EventCard: FC<{
  event: EventCard_event$key
  showEditButton?: boolean
  showOrganizer?: boolean
  showPresentationsOrAttendees?: boolean
  showCoverPhoto?: boolean
  primaryButton?: boolean
  expand?: boolean
}> = ({
  event,
  showEditButton = false,
  showOrganizer = false,
  showPresentationsOrAttendees = false,
  showCoverPhoto = true,
  primaryButton = false,
  expand = false,
}) => {
  const data = useFragment(
    graphql`
      fragment EventCard_event on Event {
        id
        rowId
        name
        slug
        prettyUrl
        startAt
        endAt
        timeZone
        myAttendance {
          id
        }
        visibility

        owner {
          __typename

          ...ActorItem_actor

          ... on User {
            isMe
          }

          ... on Guild {
            myMembership {
              membershipLevel
            }
          }
        }

        eventCardHasPresentations: presentations(
          first: 1
          orderBy: [CREATED_AT_ASC, ID_ASC]
        ) {
          totalCount
        }

        coverPhoto {
          ...GraphQLImage_image
        }

        ...AttendEventButton_event
        ...EventCardPresentationsSection_event
        ...EventCardAttendeesSection_event
      }
    `,
    event
  )

  const isEventScreen = useMatch(`/events/${data.prettyUrl}`)

  const serverContext = useContext(ServerContext)

  const [displayInTimeZone, setDisplayInTimeZone] = useState<'user' | 'event'>(
    serverContext ? 'event' : 'user'
  )

  const buttonElement = useMemo(() => {
    const isFutureEvent = new Date(data.endAt) > new Date()
    const isMyOwnEvent = data.owner?.__typename === 'User' && !!data.owner.isMe

    // Can't unattend your own event, only edit
    if (
      isFutureEvent &&
      ((isMyOwnEvent && !data.myAttendance) || !isMyOwnEvent)
    ) {
      return (
        <AttendEventButton
          key='eventAttendanceButton'
          event={data}
          primaryButton={primaryButton}
        />
      )
    }

    return (
      <Link to={`/events/${data.prettyUrl}`} key='viewButton'>
        <Button variant='secondary' hoverVariant='secondaryHover'>
          <Text variant='buttonSecondary' size={4}>
            View Event
          </Text>
        </Button>
      </Link>
    )
  }, [data])

  const parsedStartDate = useMemo(() => {
    const startAt = utcToZonedTime(
      zonedTimeToUtc(new Date(data.startAt), DEFAULT_TIMEZONE),
      displayInTimeZone === 'user' ? DEFAULT_TIMEZONE : data.timeZone
    )

    return startAt
  }, [data.startAt, data.timeZone, displayInTimeZone])

  const parsedEndDate = useMemo(() => {
    const endAt = utcToZonedTime(
      zonedTimeToUtc(new Date(data.endAt), DEFAULT_TIMEZONE),
      displayInTimeZone === 'user' ? DEFAULT_TIMEZONE : data.timeZone
    )

    return endAt
  }, [data.endAt, data.timeZone, displayInTimeZone])

  const displayTimeZone = useMemo(() => {
    if (displayInTimeZone === 'user') {
      return ''
    }

    const timeZoneOffset = getTimezoneOffset(
      data.timeZone,
      new Date(data.startAt)
    )
    const locale = timeZoneOffset < -2 * 60 * 60 * 1000 ? enUS : enGB

    return format(parsedStartDate, 'zzz', { timeZone: data.timeZone, locale })
  }, [parsedStartDate, data.startAt, data.timeZone, displayInTimeZone])

  const displayDate = useMemo(() => {
    const calendarDays = differenceInCalendarDays(
      parsedStartDate,
      parsedEndDate
    )

    if (
      calendarDays === 0 ||
      (calendarDays === -1 &&
        parsedStartDate.getHours() >= 12 &&
        parsedEndDate.getHours() < 12)
    ) {
      return (
        <>
          <Text variant='subHeading' size={4}>
            {format(parsedStartDate, 'EEEE, LLL do', {
              timeZone:
                displayInTimeZone === 'user' ? DEFAULT_TIMEZONE : data.timeZone,
            })}
          </Text>
          <Text size={4}>
            {parsedStartDate.getMinutes() === 0
              ? format(parsedStartDate, 'haa')
              : format(parsedStartDate, 'h:mmaa')}{' '}
            -{' '}
            {parsedEndDate.getMinutes() === 0
              ? format(parsedEndDate, 'haa')
              : format(parsedEndDate, 'h:mmaa')}{' '}
            {displayTimeZone}
          </Text>
        </>
      )
    }

    if (
      parsedStartDate.getMonth() === parsedEndDate.getMonth() &&
      parsedStartDate.getFullYear() === parsedEndDate.getFullYear()
    ) {
      return (
        <>
          <Text variant='subHeading' size={4}>
            {format(parsedStartDate, 'EEE, LLL do', {
              timeZone:
                displayInTimeZone === 'user' ? DEFAULT_TIMEZONE : data.timeZone,
            })}{' '}
            -{' '}
            {format(parsedEndDate, 'do', {
              timeZone:
                displayInTimeZone === 'user' ? DEFAULT_TIMEZONE : data.timeZone,
            })}
          </Text>
          <Text size={4}>
            {parsedStartDate.getMinutes() === 0
              ? format(parsedStartDate, 'haa')
              : format(parsedStartDate, 'h:mmaa')}{' '}
            -{' '}
            {parsedEndDate.getMinutes() === 0
              ? format(parsedEndDate, 'haa')
              : format(parsedEndDate, 'h:mmaa')}{' '}
            {displayTimeZone}
          </Text>
        </>
      )
    }

    return (
      <>
        <Text variant='subHeading' size={4}>
          {parsedStartDate.getMinutes() === 0
            ? format(parsedStartDate, 'EEE, LLL do haa', {
                timeZone:
                  displayInTimeZone === 'user'
                    ? DEFAULT_TIMEZONE
                    : data.timeZone,
              })
            : format(parsedStartDate, 'EEE, LLL do h:mmaa', {
                timeZone:
                  displayInTimeZone === 'user'
                    ? DEFAULT_TIMEZONE
                    : data.timeZone,
              })}{' '}
          {displayTimeZone}
        </Text>
        <Text size={4}>
          -{' '}
          {parsedEndDate.getMinutes() === 0
            ? format(parsedEndDate, 'EEE, LLL do haa', {
                timeZone:
                  displayInTimeZone === 'user'
                    ? DEFAULT_TIMEZONE
                    : data.timeZone,
              })
            : format(parsedEndDate, 'EEE, LLL do h:mmaa', {
                timeZone:
                  displayInTimeZone === 'user'
                    ? DEFAULT_TIMEZONE
                    : data.timeZone,
              })}{' '}
          {displayTimeZone}
        </Text>
      </>
    )
  }, [
    displayTimeZone,
    parsedStartDate,
    parsedEndDate,
    displayInTimeZone,
    data.timeZone,
  ])

  const actorCardTokens = useMemo(() => {
    if (expand) {
      if (!showCoverPhoto) {
        return { flex: 1, paddingTop: 4 }
      }

      return { flex: 1 }
    }

    if (!showCoverPhoto) {
      return { paddingTop: 4 }
    }

    return {}
  }, [expand, showCoverPhoto])

  return (
    <Stack expand={expand}>
      <Element component='ActorCard' tokens={actorCardTokens}>
        <Stack gap={4} expand={expand}>
          {showCoverPhoto && (
            <Link to={`/events/${data.prettyUrl}`}>
              <EventCoverPhoto event={data} />
            </Link>
          )}

          <Stack expand={expand}>
            <Element
              tokens={{
                paddingHorizontal: 4,
                paddingBottom: 0,
                ...(expand ? { flex: 1 } : {}),
              }}
            >
              <Stack gap={4} expand={expand}>
                <Stack gap={4} expand={expand}>
                  <Link to={`/events/${data.prettyUrl}`}>
                    <Text
                      size={6}
                      testID='EVENT_CARD_TITLE'
                      variant='heading'
                      numberOfLines={1}
                    >
                      {data.name}
                    </Text>
                  </Link>

                  <Link to={`/events/${data.prettyUrl}`}>
                    <Stack direction='horizontal' gap={3}>
                      <Element tokens={{ width: 8, alignItems: 'center' }}>
                        <Icon name='calendar' size={7} />
                      </Element>

                      <Stack gap={1}>
                        {isEventScreen ? (
                          <Button
                            variant='invisibleCompact'
                            onPress={() => {
                              setDisplayInTimeZone(
                                displayInTimeZone === 'user' ? 'event' : 'user'
                              )
                            }}
                            alignItems='flex-start'
                          >
                            {displayDate}
                          </Button>
                        ) : (
                          displayDate
                        )}
                      </Stack>
                    </Stack>
                  </Link>

                  {showOrganizer && !!data.owner && (
                    <ActorItem
                      actor={data.owner}
                      photoSize={8}
                      textSize={4}
                      nameOnly={true}
                    />
                  )}

                  {showPresentationsOrAttendees && (
                    <Link to={`/events/${data.prettyUrl}`}>
                      {data.eventCardHasPresentations.totalCount ? (
                        <EventCardPresentationsSection event={data} />
                      ) : (
                        <EventCardAttendeesSection event={data} />
                      )}
                    </Link>
                  )}
                </Stack>

                {buttonElement}
              </Stack>
            </Element>
          </Stack>

          {showEditButton && <EditButton data={data} />}
        </Stack>
      </Element>
    </Stack>
  )
}

const EventCoverPhoto: FC<{ event: EventCard_event }> = ({ event }) => {
  const defaultBackgroundToUse = useMemo(() => {
    if (
      event.visibility === 'DRAFT' ||
      process.env.GUILD_ENV === 'development'
    ) {
      const shortRowId = event.rowId.substring(0, 8) || '0'

      const backgroundToUse =
        BACKGROUNDS[parseInt(shortRowId, 16) % BACKGROUNDS.length]

      return backgroundToUse
    }
  }, [event.visibility, event.rowId])

  if (event.coverPhoto) {
    return (
      <GraphQLImage
        component='ActorCardBackgroundPhoto'
        image={event.coverPhoto}
        maxWidth='max'
        transformations={{
          ar: '2-1',
        }}
      />
    )
  }

  // It's too expensive to render every dynamic social card in development
  if (defaultBackgroundToUse) {
    return (
      <ImageKitImage
        component='ActorCardBackgroundPhoto'
        imageUrl={defaultBackgroundToUse}
        maxWidth='max'
        // maxHeight={30}
        transformations={{
          ar: '2-1',
        }}
      />
    )
  }

  return (
    <Image
      source={{
        uri: `${HOST_NAME}/social/event/${event.prettyUrl}/card.svg`,
      }}
      style={{
        width: '100%',
        maxHeight: 280,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
      }}
      responsive
    />
  )
}

const EditButton: FC<{
  data: EventCard_event
}> = ({ data }) => {
  const editButton = (
    <Link to={`/events/${data.prettyUrl}/settings`}>
      <Button
        key='editEvent'
        variant='callToAction'
        hoverVariant='callToActionHover'
      >
        <Stack
          direction='horizontal'
          gap={2}
          justifyContent='center'
          alignItems='center'
        >
          <Icon name='settings-filled' variant='callToActionButton' size={4} />

          <Text variant='callToActionButton' size={4}>
            Settings
          </Text>
        </Stack>
      </Button>
    </Link>
  )

  if (data.owner?.__typename === 'User' && data.owner.isMe) {
    return editButton
  }

  if (
    data.owner?.__typename === 'Guild' &&
    ['ORGANIZER', 'EDITOR'].includes(data.owner.myMembership?.membershipLevel)
  ) {
    return editButton
  }

  return null
}
